import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Plus,
  Minus,
  Trash2,
  ShoppingCart,
  UtensilsCrossed,
  Loader2,
  CheckCircle,
  AlertCircle,
  X,
  ChefHat,
  User,
} from 'lucide-react';
import OrderTracking from '../components/OrderTracking';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CustomerOrder() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  
  // URL params
  const tableId = searchParams.get('table');
  const token = searchParams.get('token');
  
  // Data state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tableInfo, setTableInfo] = useState(null);
  const [outletInfo, setOutletInfo] = useState(null);
  const [categories, setCategories] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [taxSettings, setTaxSettings] = useState(null);
  const [currencySettings, setCurrencySettings] = useState(null);
  
  // UI state
  const [activeCategory, setActiveCategory] = useState('all');
  const [cart, setCart] = useState([]);
  const [customerName, setCustomerName] = useState('');
  const [orderNotes, setOrderNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [orderSuccess, setOrderSuccess] = useState(null);
  const [activeOrders, setActiveOrders] = useState([]);  // Track existing active orders
  const [showTrackingOnly, setShowTrackingOnly] = useState(false);  // Show only tracking view
  
  // Item dialog state
  const [showItemDialog, setShowItemDialog] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [selectedAddOns, setSelectedAddOns] = useState([]);
  const [itemQuantity, setItemQuantity] = useState(1);
  const [itemNotes, setItemNotes] = useState('');

  // Format currency
  const formatCurrency = useCallback((amount) => {
    if (!currencySettings) {
      return `Rp ${Math.round(amount).toLocaleString('id-ID')}`;
    }
    const { currency_symbol, currency_position, decimal_places, thousand_separator } = currencySettings;
    const formattedAmount = Math.round(amount).toLocaleString('id-ID');
    return currency_position === 'before' 
      ? `${currency_symbol} ${formattedAmount}`
      : `${formattedAmount} ${currency_symbol}`;
  }, [currencySettings]);

  // Fetch menu data and check for active orders
  useEffect(() => {
    const fetchMenu = async () => {
      if (!tableId || !token) {
        setError('Invalid QR code. Please scan the QR code on your table.');
        setLoading(false);
        return;
      }
      
      try {
        const response = await fetch(
          `${API_URL}/api/public/menu?table_id=${tableId}&token=${token}`
        );
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.detail || 'Failed to load menu');
        }
        
        const data = await response.json();
        setTableInfo(data.table);
        setOutletInfo(data.outlet);
        setCategories(data.categories);
        setMenuItems(data.menu_items);
        setTaxSettings(data.tax_settings);
        setCurrencySettings(data.currency_settings);
        
        // Check for active orders on this table
        try {
          const ordersResponse = await fetch(
            `${API_URL}/api/public/orders/by-table/${tableId}?token=${token}`
          );
          if (ordersResponse.ok) {
            const ordersData = await ordersResponse.json();
            setActiveOrders(ordersData.orders || []);
          }
        } catch (ordersErr) {
          // Ignore - not critical
          console.log('Could not fetch active orders:', ordersErr);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    
    fetchMenu();
  }, [tableId, token]);

  // Filter menu items by category
  const filteredItems = activeCategory === 'all'
    ? menuItems
    : menuItems.filter(item => item.category_id === activeCategory);

  // Calculate cart totals
  const cartSubtotal = cart.reduce((sum, item) => {
    const itemTotal = (item.unit_price * item.quantity) + item.add_on_total;
    return sum + itemTotal;
  }, 0);

  const cartTax = taxSettings?.tax_enabled && !taxSettings?.tax_inclusive
    ? Math.round(cartSubtotal * (taxSettings.tax_rate / 100))
    : 0;

  const cartTotal = cartSubtotal + cartTax;

  // Open item dialog
  const openItemDialog = (item) => {
    setSelectedItem(item);
    setSelectedVariant(item.variants?.length > 0 ? item.variants[0] : null);
    setSelectedAddOns([]);
    setItemQuantity(1);
    setItemNotes('');
    setShowItemDialog(true);
  };

  // Calculate item price with variant
  const getItemPrice = (item, variant) => {
    if (!item) return 0;
    let price = item.base_price || 0;
    if (variant) {
      price += variant.price_adjustment || 0;
    }
    return price;
  };

  // Add to cart
  const addToCart = () => {
    if (!selectedItem) return;
    
    const addOnTotal = selectedAddOns.reduce((sum, addOn) => sum + (addOn.price || 0), 0);
    const unitPrice = getItemPrice(selectedItem, selectedVariant);
    
    const cartItem = {
      id: `${selectedItem.id}-${Date.now()}`,
      menu_item_id: selectedItem.id,
      menu_item_name: selectedItem.name,
      quantity: itemQuantity,
      unit_price: unitPrice,
      variant_id: selectedVariant?.id || null,
      variant_name: selectedVariant?.name || null,
      add_on_ids: selectedAddOns.map(a => a.id),
      add_on_names: selectedAddOns.map(a => a.name),
      add_on_total: addOnTotal,
      notes: itemNotes,
      image_url: selectedItem.image_url,
      kitchen_station: selectedItem.kitchen_station,
    };
    
    setCart([...cart, cartItem]);
    setShowItemDialog(false);
    toast.success(`${selectedItem.name} ditambahkan ke keranjang`);
  };

  // Remove from cart
  const removeFromCart = (itemId) => {
    setCart(cart.filter(item => item.id !== itemId));
  };

  // Update cart item quantity
  const updateCartQuantity = (itemId, delta) => {
    setCart(cart.map(item => {
      if (item.id === itemId) {
        const newQuantity = Math.max(1, item.quantity + delta);
        return { ...item, quantity: newQuantity };
      }
      return item;
    }));
  };

  // Submit order
  const submitOrder = async () => {
    if (cart.length === 0) {
      toast.error('Keranjang kosong');
      return;
    }
    
    setSubmitting(true);
    
    try {
      const orderData = {
        table_id: tableId,
        token: token,
        items: cart.map(item => ({
          menu_item_id: item.menu_item_id,
          menu_item_name: item.menu_item_name,
          quantity: item.quantity,
          unit_price: item.unit_price,
          variant_id: item.variant_id,
          variant_name: item.variant_name,
          add_on_ids: item.add_on_ids,
          add_on_names: item.add_on_names,
          add_on_total: item.add_on_total,
          notes: item.notes,
          kitchen_station: item.kitchen_station,
        })),
        customer_name: customerName || null,
        notes: orderNotes || null,
      };
      
      const response = await fetch(`${API_URL}/api/public/orders`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(orderData),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to place order');
      }
      
      const result = await response.json();
      setOrderSuccess(result);
      setCart([]);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin mx-auto text-primary" />
          <p className="mt-4 text-muted-foreground">Memuat menu...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">Terjadi Kesalahan</h2>
            <p className="text-muted-foreground">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Success state - Show real-time order tracking
  if (orderSuccess) {
    return (
      <div className="min-h-screen bg-background" data-testid="order-tracking-page">
        {/* Header */}
        <header className="sticky top-0 z-50 bg-card border-b shadow-sm">
          <div className="container mx-auto px-4 py-3">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-primary flex items-center justify-center">
                <UtensilsCrossed className="h-6 w-6 text-primary-foreground" />
              </div>
              <div>
                <h1 className="font-bold text-lg">{outletInfo?.name}</h1>
                <Badge variant="outline" className="mt-0.5">
                  Meja {tableInfo?.number}
                </Badge>
              </div>
            </div>
          </div>
        </header>

        <div className="container mx-auto px-4 py-6 max-w-lg">
          {/* Success Message */}
          <div className="text-center mb-6">
            <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="h-10 w-10 text-green-500" />
            </div>
            <h2 className="text-xl font-bold">Pesanan Berhasil!</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Lacak status pesanan Anda di bawah ini
            </p>
          </div>

          {/* Order Tracking Component */}
          <OrderTracking 
            orderId={orderSuccess.order_id}
            tableId={tableId}
            token={token}
            onClose={() => {
              setOrderSuccess(null);
              setCustomerName('');
              setOrderNotes('');
            }}
          />

          {/* Payment Notice */}
          <div className="mt-4 bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 text-center">
            <p className="text-sm font-medium text-amber-600 dark:text-amber-400">
              💳 Pembayaran dilakukan di kasir
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="customer-order-page">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-card border-b shadow-sm">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-primary flex items-center justify-center">
                <UtensilsCrossed className="h-6 w-6 text-primary-foreground" />
              </div>
              <div>
                <h1 className="font-bold text-lg">{outletInfo?.name}</h1>
                <Badge variant="outline" className="mt-0.5">
                  Meja {tableInfo?.number}
                </Badge>
              </div>
            </div>
            
            {/* Cart button (mobile) */}
            <Button 
              variant="outline" 
              className="relative lg:hidden"
              onClick={() => document.getElementById('cart-section')?.scrollIntoView({ behavior: 'smooth' })}
            >
              <ShoppingCart className="h-5 w-5" />
              {cart.length > 0 && (
                <span className="absolute -top-2 -right-2 bg-primary text-primary-foreground text-xs rounded-full h-5 w-5 flex items-center justify-center">
                  {cart.reduce((sum, item) => sum + item.quantity, 0)}
                </span>
              )}
            </Button>
          </div>
        </div>
        
        {/* Active Orders Banner - Shows when there are active orders */}
        {activeOrders.length > 0 && !showTrackingOnly && (
          <div className="bg-primary/10 border-t border-primary/20 px-4 py-2">
            <div className="container mx-auto flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm">
                <Loader2 className="h-4 w-4 animate-spin text-primary" />
                <span className="text-foreground font-medium">
                  {activeOrders.length} pesanan aktif
                </span>
              </div>
              <Button 
                variant="link" 
                size="sm" 
                className="text-primary h-auto p-0"
                onClick={() => setShowTrackingOnly(true)}
                data-testid="view-active-orders-btn"
              >
                Lihat Status →
              </Button>
            </div>
          </div>
        )}
      </header>

      {/* Show only tracking when showTrackingOnly is true */}
      {showTrackingOnly && activeOrders.length > 0 ? (
        <div className="container mx-auto px-4 py-6 max-w-lg">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold">Pesanan Aktif</h2>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setShowTrackingOnly(false)}
            >
              Lihat Menu
            </Button>
          </div>
          
          <div className="space-y-4">
            {activeOrders.map((order) => (
              <OrderTracking 
                key={order.order_id}
                orderId={order.order_id}
                tableId={tableId}
                token={token}
              />
            ))}
          </div>
          
          {/* Payment Notice */}
          <div className="mt-4 bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 text-center">
            <p className="text-sm font-medium text-amber-600 dark:text-amber-400">
              💳 Pembayaran dilakukan di kasir
            </p>
          </div>
        </div>
      ) : (
      <div className="container mx-auto px-4 py-4">
        <div className="lg:grid lg:grid-cols-3 lg:gap-6">
          {/* Menu Section */}
          <div className="lg:col-span-2">
            {/* Categories */}
            <ScrollArea className="w-full whitespace-nowrap pb-4">
              <div className="flex gap-2">
                <Button
                  variant={activeCategory === 'all' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setActiveCategory('all')}
                  data-testid="category-all"
                >
                  Semua
                </Button>
                {categories.map((cat) => (
                  <Button
                    key={cat.id}
                    variant={activeCategory === cat.id ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setActiveCategory(cat.id)}
                    data-testid={`category-${cat.id}`}
                  >
                    {cat.name}
                  </Button>
                ))}
              </div>
            </ScrollArea>

            {/* Menu Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {filteredItems.map((item) => (
                <Card 
                  key={item.id}
                  className={`cursor-pointer transition-all hover:shadow-md ${!item.is_available ? 'opacity-50' : ''}`}
                  onClick={() => item.is_available && openItemDialog(item)}
                  data-testid={`menu-item-${item.id}`}
                >
                  {/* Image */}
                  <div className="aspect-square bg-muted relative overflow-hidden rounded-t-lg">
                    {item.image_url ? (
                      <img 
                        src={`${API_URL}${item.image_url}`}
                        alt={item.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <ChefHat className="h-12 w-12 text-muted-foreground/30" />
                      </div>
                    )}
                    {!item.is_available && (
                      <div className="absolute inset-0 bg-background/80 flex items-center justify-center">
                        <Badge variant="destructive">Habis</Badge>
                      </div>
                    )}
                  </div>
                  <CardContent className="p-3">
                    <h3 className="font-medium text-sm line-clamp-2">{item.name}</h3>
                    <p className="text-primary font-bold mt-1">
                      {formatCurrency(item.base_price)}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>

            {filteredItems.length === 0 && (
              <div className="text-center py-12">
                <ChefHat className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">Tidak ada menu di kategori ini</p>
              </div>
            )}
          </div>

          {/* Cart Section */}
          <div id="cart-section" className="mt-6 lg:mt-0">
            <Card className="sticky top-24">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2">
                  <ShoppingCart className="h-5 w-5" />
                  Keranjang
                </CardTitle>
                {cart.length > 0 && (
                  <CardDescription>
                    {cart.reduce((sum, item) => sum + item.quantity, 0)} item
                  </CardDescription>
                )}
              </CardHeader>
              <CardContent>
                {cart.length === 0 ? (
                  <div className="text-center py-8">
                    <ShoppingCart className="h-12 w-12 mx-auto text-muted-foreground/30 mb-3" />
                    <p className="text-muted-foreground text-sm">Keranjang kosong</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {cart.map((item) => (
                      <div key={item.id} className="flex gap-3 p-2 bg-muted/50 rounded-lg">
                        {/* Item thumbnail */}
                        <div className="w-16 h-16 rounded-md bg-muted overflow-hidden shrink-0">
                          {item.image_url ? (
                            <img 
                              src={`${API_URL}${item.image_url}`}
                              alt={item.menu_item_name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <ChefHat className="h-6 w-6 text-muted-foreground/30" />
                            </div>
                          )}
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-sm truncate">{item.menu_item_name}</h4>
                          {item.variant_name && (
                            <p className="text-xs text-muted-foreground">{item.variant_name}</p>
                          )}
                          <p className="text-sm font-bold text-primary">
                            {formatCurrency((item.unit_price * item.quantity) + item.add_on_total)}
                          </p>
                          
                          <div className="flex items-center gap-2 mt-1">
                            <Button
                              variant="outline"
                              size="icon"
                              className="h-6 w-6"
                              onClick={() => updateCartQuantity(item.id, -1)}
                            >
                              <Minus className="h-3 w-3" />
                            </Button>
                            <span className="text-sm font-medium w-6 text-center">{item.quantity}</span>
                            <Button
                              variant="outline"
                              size="icon"
                              className="h-6 w-6"
                              onClick={() => updateCartQuantity(item.id, 1)}
                            >
                              <Plus className="h-3 w-3" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 text-destructive ml-auto"
                              onClick={() => removeFromCart(item.id)}
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}

                    {/* Customer Info */}
                    <div className="border-t pt-3 mt-3">
                      <div className="space-y-3">
                        <div>
                          <Label className="text-xs">Nama (opsional)</Label>
                          <Input
                            value={customerName}
                            onChange={(e) => setCustomerName(e.target.value)}
                            placeholder="Nama Anda"
                            className="h-9 mt-1"
                            data-testid="customer-name-input"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Catatan (opsional)</Label>
                          <Textarea
                            value={orderNotes}
                            onChange={(e) => setOrderNotes(e.target.value)}
                            placeholder="Catatan untuk pesanan..."
                            rows={2}
                            className="mt-1"
                            data-testid="order-notes-input"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Totals */}
                    <div className="border-t pt-3 space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Subtotal</span>
                        <span>{formatCurrency(cartSubtotal)}</span>
                      </div>
                      {taxSettings?.tax_enabled && !taxSettings?.tax_inclusive && (
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">
                            {taxSettings.tax_name} ({taxSettings.tax_rate}%)
                          </span>
                          <span>{formatCurrency(cartTax)}</span>
                        </div>
                      )}
                      <div className="flex justify-between font-bold text-lg">
                        <span>Total</span>
                        <span className="text-primary">{formatCurrency(cartTotal)}</span>
                      </div>
                    </div>

                    {/* Submit Button */}
                    <Button 
                      className="w-full h-12"
                      size="lg"
                      onClick={submitOrder}
                      disabled={submitting || cart.length === 0}
                      data-testid="submit-order-btn"
                    >
                      {submitting ? (
                        <>
                          <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                          Memproses...
                        </>
                      ) : (
                        <>
                          <CheckCircle className="h-5 w-5 mr-2" />
                          Pesan Sekarang
                        </>
                      )}
                    </Button>

                    <p className="text-xs text-center text-muted-foreground">
                      Pembayaran dilakukan di kasir
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
      )}

      {/* Item Detail Dialog */}
      <Dialog open={showItemDialog} onOpenChange={setShowItemDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{selectedItem?.name}</DialogTitle>
            {selectedItem?.description && (
              <DialogDescription>{selectedItem.description}</DialogDescription>
            )}
          </DialogHeader>

          <div className="space-y-4">
            {/* Item Image */}
            {selectedItem?.image_url && (
              <div className="aspect-video rounded-lg overflow-hidden bg-muted">
                <img 
                  src={`${API_URL}${selectedItem.image_url}`}
                  alt={selectedItem.name}
                  className="w-full h-full object-cover"
                />
              </div>
            )}

            {/* Variants */}
            {selectedItem?.variants?.length > 0 && (
              <div>
                <Label className="text-sm font-medium">Pilih Varian</Label>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {selectedItem.variants.map((variant) => (
                    <Button
                      key={variant.id}
                      variant={selectedVariant?.id === variant.id ? 'default' : 'outline'}
                      size="sm"
                      className="justify-between"
                      onClick={() => setSelectedVariant(variant)}
                    >
                      <span>{variant.name}</span>
                      {variant.price_adjustment > 0 && (
                        <span className="text-xs">+{formatCurrency(variant.price_adjustment)}</span>
                      )}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            {/* Add-ons */}
            {selectedItem?.add_ons?.length > 0 && (
              <div>
                <Label className="text-sm font-medium">Tambahan</Label>
                <div className="space-y-2 mt-2">
                  {selectedItem.add_ons.map((addOn) => (
                    <div 
                      key={addOn.id}
                      className={`flex items-center justify-between p-2 rounded-lg border cursor-pointer ${
                        selectedAddOns.some(a => a.id === addOn.id) 
                          ? 'border-primary bg-primary/10' 
                          : 'border-border'
                      }`}
                      onClick={() => {
                        if (selectedAddOns.some(a => a.id === addOn.id)) {
                          setSelectedAddOns(selectedAddOns.filter(a => a.id !== addOn.id));
                        } else {
                          setSelectedAddOns([...selectedAddOns, addOn]);
                        }
                      }}
                    >
                      <span className="text-sm">{addOn.name}</span>
                      <span className="text-sm font-medium">+{formatCurrency(addOn.price)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Notes */}
            <div>
              <Label className="text-sm font-medium">Catatan</Label>
              <Textarea
                value={itemNotes}
                onChange={(e) => setItemNotes(e.target.value)}
                placeholder="Contoh: tanpa sambal, pedas level 2"
                rows={2}
                className="mt-1"
              />
            </div>

            {/* Quantity */}
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium">Jumlah</Label>
              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setItemQuantity(Math.max(1, itemQuantity - 1))}
                >
                  <Minus className="h-4 w-4" />
                </Button>
                <span className="w-8 text-center font-bold">{itemQuantity}</span>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setItemQuantity(itemQuantity + 1)}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowItemDialog(false)}>
              Batal
            </Button>
            <Button onClick={addToCart} data-testid="add-to-cart-btn">
              <Plus className="h-4 w-4 mr-2" />
              Tambah {formatCurrency(
                (getItemPrice(selectedItem, selectedVariant) * itemQuantity) + 
                selectedAddOns.reduce((sum, a) => sum + (a.price || 0), 0)
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
